import React, { useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { ReactComponent as DictionarySearchIcon } from "icons/dictionary_search.svg";
import Button, {
  buttonVariants,
} from "@visiblethread/react-components/dist/components/Button";
import Dropdown, {
  dropdownTypes,
} from "@visiblethread/react-components/dist/components/Dropdown";
import ButtonsDropdownOption from "@visiblethread/react-components/dist/components/Dropdown/ButtonsDropdownOption";
import MegaToggle from "@visiblethread/react-components/dist/components/MegaToggle";
import { htmlDecode, isDocx, isPdf, isWord } from "utils";
import { useDispatch, useSelector } from "react-redux";
import { exportOrigin, folderBy } from "layout/dictionarySearch/constants";
import { setFolderBy } from "slices/dictionarySearch/dictionarySearchSlice";
import {
  exportAllPdfDocument,
  exportAllPdfFolder,
  exportContentCSV,
  exportResultsCSVDocument,
  exportResultsCSVFolder,
} from "slices/dictionarySearch/actions/dictionarySearch";
import { showModal } from "slices/app/actions/modals";
import { checkedTypes, modalTypes } from "constants/types";
import { getDocList, getFolderByFolderDocList } from "utils/dictionarySearch";
import {
  getDocIdItemsArrayList,
  getDocItemsArrayList,
} from "layout/dictionarySearch/content/ContentPanel";

export const TOGGLE_OPTIONS = ["By Folder", "By Document"];

const onMessageImpl = (
  event,
  documents,
  extRef,
  handleExport,
  actionRequiredMessage
) => {
  if (actionRequiredMessage || actionRequiredMessage !== "") {
    return;
  }
  if (window.PageObject.extRef !== extRef) {
    return;
  }
  if (window.PageObject.lastEvent === event) {
    return;
  }
  window.PageObject.lastEvent = event;
  if (window.counter[extRef] < 1) {
    return;
  }
  if (documents.length !== 0) {
    window.counter[extRef] = 0;
    window.PageObject.received = true;
    handleExport(window.PageObject.action);
  }
};

const extensions = (title) => {
  let pdfSelected = false;
  let wordSelected = false;
  if (isPdf(title)) {
    pdfSelected = true;
  }
  if (isWord(title)) {
    wordSelected = true;
  }
  return { pdfSelected, wordSelected };
};

const useEffectImpl = (onMessage, extRef) => {
  if (window[`hander_${extRef}`] !== true) {
    window[`hander_${extRef}`] = true;
    window.addEventListener("message", onMessage, false);
  }
  return () => {
    window[`hander_${extRef}`] = null;
    window.removeEventListener("message", onMessage, false);
  };
};

const DictionarySearchHeader = ({ title, isDocView, extRef }) => {
  const by = useSelector((state) => state.dictionarySearch.config.folderBy);
  const dispatch = useDispatch();
  const documents = useSelector((state) => {
    if (by === folderBy.folder) {
      return state.dictionarySearch.folder.documents;
    }
    return state.dictionarySearch.documents;
  });
  const folderCategories = useSelector(
    (state) => state.dictionarySearch.folder?.dictionary.categories
  );
  const { folder } = useSelector((state) => state.dictionarySearch);
  const actionRequiredMessage = useSelector(
    (state) => state.app.actionRequired.message
  );
  const checkedItems = () => {
    const result = [];
    if (by === folderBy.document) {
      documents.forEach((d) =>
        d.dictionary.categories.forEach((c) =>
          c.items?.forEach((item) => {
            if (item.checkState === checkedTypes.CHECKED) {
              result.push(item.id);
            }
          })
        )
      );
    }
    if (by === folderBy.folder) {
      folderCategories.forEach((c) =>
        c.items?.forEach((i) => {
          if (i.checkState === checkedTypes.CHECKED) {
            result.push(i.id);
          }
        })
      );
    }
    return result;
  };
  const handleExport = (origin) => {
    if (documents.length > 1) {
      dispatch(
        showModal(modalTypes.EXPORT_MULTIPLE_DOCS, {
          checkedItems: checkedItems(),
          origin,
        })
      );
    } else if (documents.length === 1) {
      dispatch(
        showModal(modalTypes.EXPORT_CONTENT, {
          documentsWithTerms: getDocIdItemsArrayList(
            by,
            checkedItems(),
            documents
          ),
          origin,
          extensions: extensions(title),
        })
      );
    }
  };
  const docList = useMemo(() => {
    if (isDocView) {
      return getDocList(documents);
    }
    return by === folderBy.folder
      ? getFolderByFolderDocList(folder)
      : getDocList(documents);
  }, [by, folder, documents, isDocView]);
  const handleDownloadAnnotatedDoc = () => {
    dispatch(
      showModal(modalTypes.ANNOTATED_DOCUMENT, {
        selectedTerms: getDocItemsArrayList(documents)[0]?.selectedTerms,
      })
    );
  };
  const handleExportContentCSV = () => {
    dispatch(exportContentCSV(title, isDocView, docList));
  };
  const handleExportResultsCSV = () => {
    if (isDocView) {
      dispatch(exportResultsCSVDocument(title, checkedItems()));
    } else {
      dispatch(exportResultsCSVFolder(title, checkedItems()));
    }
  };
  const annotatedDocumentButton = () =>
    isDocx(title) ? (
      <Button
        variant={buttonVariants.secondary}
        content="Download Annotated Document"
        onClick={handleDownloadAnnotatedDoc}
        dataTestId="download-annotated-document-button"
      />
    ) : (
      <Button
        variant={buttonVariants.secondary}
        content="Download Annotated Document"
        onClick={() => {}}
        dataTestId="download-annotated-document-button"
        disabled
        title="Not available. This option is only available for Word .docx files."
      />
    );
  const exportResultsCsvButton = () =>
    by === folderBy.folder || isDocView ? (
      <ButtonsDropdownOption
        content="Export Left Grid (csv)"
        onClick={handleExportResultsCSV}
      />
    ) : (
      <ButtonsDropdownOption
        content={
          <span title="Option not available.">Export Left Grid (csv)</span>
        }
        onClick={() => {}}
        disabled
      />
    );
  const exportPdfButton = () =>
    by === folderBy.document && !isDocView ? (
      <ButtonsDropdownOption
        content={<span title="Option not available.">Export All (pdf)</span>}
        onClick={() => {}}
        disabled
      />
    ) : (
      <ButtonsDropdownOption
        content="Export All (pdf)"
        onClick={() => {
          dispatch(isDocView ? exportAllPdfDocument() : exportAllPdfFolder());
        }}
      />
    );
  const exportOptions = () => [
    exportResultsCsvButton(),
    <ButtonsDropdownOption
      content="Export Content (csv)"
      onClick={handleExportContentCSV}
      disabled={docList.length === 0}
    />,
    exportPdfButton(),
  ];

  const shredOptions = () => [
    <ButtonsDropdownOption
      content="Simple Shred (Compliance Matrix)"
      onClick={() => handleExport(exportOrigin.simpleShred)}
    />,
    <ButtonsDropdownOption
      content="Category Shred"
      onClick={() => handleExport(exportOrigin.catShred)}
    />,
  ];

  const onMessage = useCallback((event) =>
    onMessageImpl(event, documents, extRef, handleExport, actionRequiredMessage)
  );

  const handleToggleClick = () => {
    if (by === folderBy.folder) {
      dispatch(setFolderBy(folderBy.document));
    } else {
      dispatch(setFolderBy(folderBy.folder));
    }
  };

  // eslint-disable-next-line
  useEffect(() => useEffectImpl(onMessage, extRef), [onMessage]);

  return (
    <div
      className="dictionary_search__header"
      data-testid="dictionary-search-header"
    >
      <div className="dictionary_search__header__left">
        <span className="dictionary_search__header__left__name">
          <DictionarySearchIcon />
          <b title={htmlDecode(title)}>{htmlDecode(title)}</b>
        </span>
        {!isDocView && (
          <MegaToggle
            options={TOGGLE_OPTIONS}
            selectedOption={
              by === folderBy.folder ? TOGGLE_OPTIONS[0] : TOGGLE_OPTIONS[1]
            }
            onClick={handleToggleClick}
          />
        )}
      </div>
      <div className="dictionary_search__header__right">
        <Dropdown
          type={dropdownTypes.buttons}
          propsObject={{
            button: <span>Shred to Excel</span>,
            options: shredOptions(),
            buttonVariant: buttonVariants.secondary,
          }}
        />
        {isDocView && annotatedDocumentButton()}
        <Dropdown
          type={dropdownTypes.buttons}
          propsObject={{
            button: <span>Export</span>,
            options: exportOptions(),
            buttonVariant: buttonVariants.secondary,
          }}
        />
      </div>
    </div>
  );
};

export default DictionarySearchHeader;

DictionarySearchHeader.propTypes = {
  title: PropTypes.string.isRequired,
  isDocView: PropTypes.bool.isRequired,
  extRef: PropTypes.string,
};
